import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchGroupStats, type AdminGroupStats } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A4.17 — Guruh/kanal statistikasi. GET /admin/groups/:id/stats (backend `46dc8f2`).
 * PII-siz agregat: a'zolar/o'sish, 7-kun xabar qatori, top-posterlar.
 */
export function GroupStats() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [stats, setStats] = useState<AdminGroupStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchGroupStats(Number(id), ac.signal)
      .then((s) => {
        if (alive) setStats(s)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  const bars = useMemo<BarDatum[]>(
    () =>
      (stats?.messages_per_day ?? []).map((d) => ({ label: d.day.slice(5), value: d.messages, title: d.day })),
    [stats],
  )

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={t.groups.stats}
        screenId="A4.17"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
              <KPI label={t.groups.members} value={stats?.member_count} loading={loading} accent />
              <KPI label={t.groups.admins} value={stats?.admin_count} loading={loading} />
              <KPI label={t.groups.new7d} value={stats?.new_members.d7} loading={loading} />
              <KPI label={t.groups.messages7d} value={stats?.messages_7d} loading={loading} />
              <KPI label={t.groups.messagesTotal} value={stats?.messages_total} loading={loading} />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
              <Panel title={t.dash.messagesPerDay}>
                {loading ? (
                  <Skeleton className="h-40 w-full" />
                ) : (
                  <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
                )}
              </Panel>

              <Panel title={t.groups.topPosters}>
                {loading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Skeleton key={i} className="h-5 w-full" />
                    ))}
                  </div>
                ) : !stats?.top_posters.length ? (
                  <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
                ) : (
                  <div className="divide-y divide-border-subtle">
                    {stats.top_posters.map((p, i) => (
                      <div key={p.id} className="flex items-center gap-3 py-1.5 text-sm">
                        <span className="w-5 shrink-0 font-mono text-tertiary">{i + 1}</span>
                        <Link to={`/users/${p.id}`} className="flex-1 truncate text-accent hover:underline">
                          {p.first_name || (p.username ? `@${p.username}` : `#${p.id}`)}
                        </Link>
                        <span className="shrink-0 font-mono tabular text-primary">
                          {p.messages.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  accent,
}: {
  label: string
  value: number | undefined
  loading: boolean
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-14" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}
